import React from 'react';

type IconProps = { size?: number; animated?: boolean };

export const SunIcon = ({ size = 24, animated = true }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Encapsulated animations + reduced-motion fallback */}
    <style>{`
      @keyframes sun-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes sun-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
      @keyframes cloud-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-0.8px); } }
      @keyframes rain-drop { 0% { transform: translateY(0); opacity: 1; } 80% { opacity: 1; } 100% { transform: translateY(6px); opacity: 0; } }
      @media (prefers-reduced-motion: reduce) {
        .no-motion { animation: none !important; }
      }
    `}</style>
    <circle
      cx="12"
      cy="12"
      r="5"
      fill="currentColor"
      className={animated ? 'no-motion' : undefined}
      style={animated ? { transformOrigin: '12px 12px', animation: 'sun-pulse 3.5s ease-in-out infinite' } : undefined}
    />
    <g
      stroke="currentColor"
      strokeWidth="2"
      className={animated ? 'no-motion' : undefined}
      style={animated ? { transformOrigin: '12px 12px', animation: 'sun-spin 16s linear infinite' } : undefined}
    >
      <line
        x1="12"
        y1="1"
        x2="12"
        y2="4"
      />
      <line
        x1="12"
        y1="20"
        x2="12"
        y2="23"
      />
      <line
        x1="1"
        y1="12"
        x2="4"
        y2="12"
      />
      <line
        x1="20"
        y1="12"
        x2="23"
        y2="12"
      />
      <line
        x1="4.22"
        y1="4.22"
        x2="6.34"
        y2="6.34"
      />
      <line
        x1="17.66"
        y1="17.66"
        x2="19.78"
        y2="19.78"
      />
      <line
        x1="4.22"
        y1="19.78"
        x2="6.34"
        y2="17.66"
      />
      <line
        x1="17.66"
        y1="6.34"
        x2="19.78"
        y2="4.22"
      />
    </g>
  </svg>
);

export const CloudIcon = ({ size = 24, animated = true }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <style>{`
      @keyframes cloud-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-0.8px); } }
      @media (prefers-reduced-motion: reduce) { .no-motion { animation: none !important; } }
    `}</style>
    <path
      d="M6 18h11a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.7-1.5A4.5 4.5 0 0 0 6 18z"
      fill="currentColor"
      className={animated ? 'no-motion' : undefined}
      style={animated ? { transformOrigin: '12px 14px', animation: 'cloud-bob 5s ease-in-out infinite' } : undefined}
    />
  </svg>
);

export const RainIcon = ({ size = 24, animated = true }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <style>{`
      @keyframes cloud-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-0.6px); } }
      @keyframes rain-drop { 0% { transform: translateY(0); opacity: 1; } 80% { opacity: 1; } 100% { transform: translateY(6px); opacity: 0; } }
      @media (prefers-reduced-motion: reduce) { .no-motion { animation: none !important; } }
    `}</style>
    <path
      d="M6 15h10a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.7-1.5A4.5 4.5 0 0 0 6 15z"
      fill="currentColor"
      className={animated ? 'no-motion' : undefined}
      style={animated ? { transformOrigin: '12px 12px', animation: 'cloud-bob 4.5s ease-in-out infinite' } : undefined}
    />
    <g
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line
        x1="8"
        y1="17"
        x2="8"
        y2="21"
        className={animated ? 'no-motion' : undefined}
        style={animated ? { animation: 'rain-drop 900ms linear infinite', animationDelay: '0ms' } : undefined}
      />
      <line
        x1="12"
        y1="17"
        x2="12"
        y2="21"
        className={animated ? 'no-motion' : undefined}
        style={animated ? { animation: 'rain-drop 900ms linear infinite', animationDelay: '150ms' } : undefined}
      />
      <line
        x1="16"
        y1="17"
        x2="16"
        y2="21"
        className={animated ? 'no-motion' : undefined}
        style={animated ? { animation: 'rain-drop 900ms linear infinite', animationDelay: '300ms' } : undefined}
      />
    </g>
  </svg>
);

// Extra mini icons for metric pills
export const DropletIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 2C12 2 6 9 6 13.5C6 17.09 8.91 20 12.5 20C16.09 20 19 17.09 19 13.5C19 9 12 2 12 2Z"
      fill="currentColor"
    />
  </svg>
);

export const WindIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3 8h10a3 3 0 1 0-3-3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M3 12h14a3 3 0 1 1-3 3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const UvIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Simple sun icon to avoid font/text rendering issues */}
    <circle
      cx="12"
      cy="12"
      r="4"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <g
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line
        x1="12"
        y1="2"
        x2="12"
        y2="5"
      />
      <line
        x1="12"
        y1="19"
        x2="12"
        y2="22"
      />
      <line
        x1="2"
        y1="12"
        x2="5"
        y2="12"
      />
      <line
        x1="19"
        y1="12"
        x2="22"
        y2="12"
      />
      <line
        x1="5.5"
        y1="5.5"
        x2="7.5"
        y2="7.5"
      />
      <line
        x1="16.5"
        y1="16.5"
        x2="18.5"
        y2="18.5"
      />
      <line
        x1="5.5"
        y1="18.5"
        x2="7.5"
        y2="16.5"
      />
      <line
        x1="16.5"
        y1="7.5"
        x2="18.5"
        y2="5.5"
      />
    </g>
  </svg>
);

// Distinct precipitation icon to differentiate from humidity
export const PrecipIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 14h9a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.6-1.6A4 4 0 0 0 6 14z"
      fill="currentColor"
      opacity="0.25"
    />
    <g
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <line
        x1="9"
        y1="16"
        x2="9"
        y2="20"
      />
      <line
        x1="13"
        y1="16"
        x2="13"
        y2="20"
      />
    </g>
  </svg>
);
